import React, { useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import DraggableHotspot from './DraggableHotspot';

const DroppableArea = ({ image, hotspots, selectedHotspot, onHotspotSelect, onHotspotChange, onAddHotspot }) => {
    const areaRef = useRef(null);

    const [{ isOver }, drop] = useDrop({
        accept: 'HOTSPOT',
        drop: (item, monitor) => {
            const delta = monitor.getDifferenceFromInitialOffset();
            const hotspot = hotspots.find(h => h.id === item.id);

            if (delta && hotspot) {
                const left = Math.round(hotspot.x + delta.x);
                const top = Math.round(hotspot.y + delta.y);

                // 获取图片容器的边界
                const bounds = areaRef.current.getBoundingClientRect();

                // 确保热区不会超出图片边界
                const newX = Math.max(0, Math.min(left, bounds.width - hotspot.width));
                const newY = Math.max(0, Math.min(top, bounds.height - hotspot.height));

                onHotspotChange(hotspot.id, {
                    x: newX,
                    y: newY
                });
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver()
        })
    });

    drop(areaRef);

    const [isDragging, setIsDragging] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });

    const handleMouseDown = (e) => {
        // 如果点击的是热区或调整手柄，不处理
        if (e.target.closest('.hotspot')) return;

        e.preventDefault();
        const rect = areaRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        setIsDragging(true);
        setStartPos({ x, y });
        setCurrentPos({ x, y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setCurrentPos({ x, y });
    };

    const handleMouseUp = (e) => {
        if (!isDragging) return;
        e.preventDefault();

        setIsDragging(false);

        // 计算热区的位置和大小
        const x = Math.min(startPos.x, currentPos.x);
        const y = Math.min(startPos.y, currentPos.y);
        const width = Math.abs(currentPos.x - startPos.x);
        const height = Math.abs(currentPos.y - startPos.y);

        // 如果拖动的距离太小，不创建热区
        if (width < 20 || height < 20) return;

        // 创建新的热区
        const newHotspot = {
            id: Date.now(),
            x,
            y,
            width,
            height,
            link: ''
        };

        // 添加新热区
        onAddHotspot && onAddHotspot(newHotspot);
        onHotspotSelect(newHotspot);
    };

    return (
        <div
            className="imageWrapper"
            ref={areaRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
                position: 'relative',
                display: 'inline-block',
                userSelect: 'none',
                WebkitUserSelect: 'none'
            }}
        >
            <img
                src={image}
                alt="编辑图片"
                style={{
                    display: 'block',
                    maxWidth: '100%',
                    height: 'auto'
                }}
            />
            {isDragging && (
                <div
                    className="hotspot"
                    style={{
                        left: Math.min(startPos.x, currentPos.x) + 'px',
                        top: Math.min(startPos.y, currentPos.y) + 'px',
                        width: Math.abs(currentPos.x - startPos.x) + 'px',
                        height: Math.abs(currentPos.y - startPos.y) + 'px',
                        background: 'rgba(0, 82, 217, 0.1)',
                        border: '2px solid var(--semi-color-primary)',
                        position: 'absolute',
                        pointerEvents: 'none'
                    }}
                />
            )}
            {hotspots.map(hotspot => (
                <DraggableHotspot
                    key={hotspot.id}
                    hotspot={hotspot}
                    isSelected={selectedHotspot?.id === hotspot.id}
                    onSelect={onHotspotSelect}
                    onChange={(changes) => onHotspotChange(hotspot.id, changes)}
                />
            ))}
        </div>
    );
};

export default DroppableArea;